/** Platform-admin — cross-tenant invoices + reconciliation (P5).
 *
 * Staff mark bank/off-Stripe payments paid + void mistakes. Filter by
 * status or overdue. */
import { useState } from "react";
import { formatDate } from "@/lib/format";
import { useTranslation } from "react-i18next";

import {
  usePlatformInvoiceAction,
  usePlatformInvoices,
} from "@/lib/platform";

const FILTERS = [
  { key: "", label: "platform.invoices.all" },
  { key: "status=open", label: "platform.invoices.open" },
  { key: "overdue=1", label: "platform.invoices.overdue" },
  { key: "status=paid", label: "platform.invoices.paid" },
];

export default function PlatformInvoicesPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const invoices = usePlatformInvoices(filter);
  const markPaid = usePlatformInvoiceAction("mark-paid");
  const voidInv = usePlatformInvoiceAction("void");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-nb-text">
          {t("platform.invoices.title")}
        </h1>
        <p className="text-sm text-nb-text-3">{t("platform.invoices.lead")}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`text-xs ${filter === f.key ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setFilter(f.key)}
          >
            {t(f.label)}
          </button>
        ))}
      </div>

      <section className="card">
        {invoices.isLoading ? (
          <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
        ) : !invoices.data || invoices.data.length === 0 ? (
          <p className="text-sm text-nb-text-3">{t("platform.invoices.none")}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-nb-text-3">
              <tr>
                <th className="py-1">{t("platform.invoices.billTo")}</th>
                <th className="py-1">{t("platform.invoices.model")}</th>
                <th className="py-1">{t("admin.billing.invoicePeriod")}</th>
                <th className="py-1 text-right">{t("admin.billing.invoiceTotal")}</th>
                <th className="py-1">{t("admin.billing.invoiceStatusHdr")}</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {invoices.data.map((inv) => (
                <tr key={inv.id} className="border-t border-nb-border">
                  <td className="py-1.5 text-nb-text">{inv.bill_to || "—"}</td>
                  <td className="py-1.5 text-nb-text-3">
                    {t(
                      `platform.invoices.modelOption.${inv.billing_model}`,
                      inv.billing_model ?? "",
                    )}
                  </td>
                  <td className="py-1.5 text-nb-text-2">
                    {formatDate(inv.period_start)}
                  </td>
                  <td className="py-1.5 text-right text-nb-text">
                    €{inv.total_eur}
                  </td>
                  <td className="py-1.5">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        inv.status === "paid"
                          ? "bg-nb-mint/15 text-nb-mint"
                          : inv.status === "void"
                            ? "bg-slate-700/40 text-nb-text-3"
                            : "bg-amber-500/15 text-amber-300"
                      }`}
                    >
                      {t(`platform.invoices.statusOption.${inv.status}`, inv.status)}
                    </span>
                  </td>
                  <td className="py-1.5 text-right">
                    {inv.status !== "paid" && inv.status !== "void" && (
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          disabled={markPaid.isPending}
                          onClick={() => markPaid.mutate(inv.id)}
                        >
                          {t("partner.overview.pnlMarkPaid")}
                        </button>
                        <button
                          type="button"
                          className="text-xs text-nb-text-3 hover:text-cvp-danger"
                          disabled={voidInv.isPending}
                          onClick={() => {
                            if (window.confirm(t("platform.invoices.voidConfirm")))
                              voidInv.mutate(inv.id);
                          }}
                        >
                          {t("platform.invoices.void")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
